import DOMPurify from 'dompurify';
import {
  ArrowLeftRight,
  ArrowRight,
  Bookmark,
  Bot,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  File,
  Folder,
  GitBranch,
  GitFork,
  Globe,
  Info,
  ListChecks,
  MessageCircle,
  Pencil,
  Search,
  Terminal,
} from 'lucide-react';
import { marked } from 'marked';
import { memo, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AgentConfigDTO, EventPayload, GateDTO, MessageDTO, ToolCallDTO } from '@harness/shared';
import { GithubIcon, JiraIcon, Spinner } from '../../components/ui';
import { api, errorText } from '../../lib/api';
import type { ConversationData } from '../../lib/events';
import { useAgents, useSession, type ProjectDetail } from '../../lib/queries';
import { useCan, VIEWER_HINT } from '../../lib/role';
import { toast, useUI } from '../../lib/store';
import type { CtxTab } from './ContextPanel';
import { gateActions } from './FeatureDialogs';

marked.setOptions({ gfm: true, breaks: false });

const Markdown = memo(function Markdown({ text }: { text: string }) {
  const html = useMemo(() => DOMPurify.sanitize(marked.parse(text, { async: false }) as string, { ADD_ATTR: ['target'] }), [text]);
  return <div className="md" dangerouslySetInnerHTML={{ __html: html }} />;
});

const TOOL_ICON: Record<string, typeof Search> = {
  search: Search,
  read_file: File,
  list_files: Folder,
  write_file: Pencil,
  edit_file: Pencil,
  run_command: Terminal,
  git: GitBranch,
  http_fetch: Globe,
  web_search: Globe,
  web_fetch: Globe,
  remember: Bookmark,
  search_history: MessageCircle,
  handoff: ArrowLeftRight,
  submit_plan: ListChecks,
};

const ASK_VERB: Record<string, string> = {
  run_command: 'run a shell command',
  write_file: 'write a file',
  edit_file: 'patch a file',
  git: 'run a git command',
  http_fetch: 'fetch a URL',
  list_files: 'list files',
  read_file: 'read a file',
  search: 'search files',
};

const GROUP_LABEL: Record<string, string> = { read: 'file read', write: 'file write', shell: 'shell', git: 'git', web: 'web search', http: 'HTTP fetch' };

/** Colours unified-diff-looking output (patch results). */
function DiffText({ text }: { text: string }) {
  if (!/^(@@|[-+] )/m.test(text)) return <>{text}</>;
  return (
    <>
      {text.split('\n').map((l, i) => (
        <span key={i} className={l.startsWith('@@') ? 'hunk' : l.startsWith('+ ') || l.startsWith('+') && !l.startsWith('+++') ? 'add' : l.startsWith('- ') || (l.startsWith('-') && !l.startsWith('---')) ? 'del' : undefined}>
          {l || ' '}
          {'\n'}
        </span>
      ))}
    </>
  );
}

function ToolCard({ t }: { t: ToolCallDTO }) {
  const [open, setOpen] = useState(false);
  const Icon = TOOL_ICON[t.tool] ?? Terminal;
  const running = t.status === 'running' || t.status === 'pending';
  const failed = t.status === 'error';
  return (
    <div className="tool">
      <button className={`tool-head ${failed ? 'err' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="tool-icon">
          <Icon className="icon" />
        </span>
        <span className="name">{t.tool}</span>
        <span className="input">{t.display}</span>
        <span className="meta">{running ? <Spinner /> : failed ? 'failed' : t.meta}</span>
        {open ? <ChevronDown className="icon faint" /> : <ChevronRight className="icon faint" />}
      </button>
      {open && (
        <pre className="tool-out">
          <DiffText text={t.output || (running ? 'Running…' : '(no output)')} />
        </pre>
      )}
    </div>
  );
}

function ApprovalCard({ t, agentName }: { t: ToolCallDTO; agentName: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const can = useCan();
  const myName = useSession().data?.user?.name;
  // approvedBy is the approver's name ("You" on older rows).
  const by = !t.approvedBy || t.approvedBy === 'You' || t.approvedBy === myName ? 'you' : t.approvedBy;
  const pending = t.status === 'awaiting_approval';
  const decide = async (kind: 'allow' | 'always' | 'deny') => {
    setBusy(kind);
    try {
      if (kind === 'deny') await api.post(`/api/tool-calls/${t.id}/deny`, {});
      else await api.post(`/api/tool-calls/${t.id}/approve`, { always: kind === 'always' });
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(null);
    }
  };
  const secs = t.durationMs != null ? ` · ${(t.durationMs / 1000).toFixed(1)} s` : '';
  const head = pending
    ? `Approval needed · ${agentName} wants to ${ASK_VERB[t.tool] ?? `use ${t.tool}`}`
    : t.status === 'running'
      ? `Running · approved by ${by}`
      : t.status === 'denied'
        ? `Denied by ${by}`
        : t.status === 'error'
          ? `Failed · approved by ${by}${secs}`
          : `Completed · approved by ${by}${secs}`;
  const body =
    t.tool === 'run_command'
      ? `$ ${t.display}`
      : t.tool === 'write_file'
        ? `${String(t.input.path)}\n${String(t.input.content ?? '').split('\n').slice(0, 14).join('\n')}${String(t.input.content ?? '').split('\n').length > 14 ? '\n…' : ''}`
        : t.tool === 'edit_file'
          ? `${String(t.input.path)}\n${String(t.input.old_string ?? '')
              .split('\n')
              .slice(0, 8)
              .map((l) => `- ${l}`)
              .join('\n')}\n${String(t.input.new_string ?? '')
              .split('\n')
              .slice(0, 8)
              .map((l) => `+ ${l}`)
              .join('\n')}`
          : t.tool === 'git'
            ? `$ ${t.display}`
            : t.display;
  return (
    <div className={`gate ${pending ? 'pending' : ''}`}>
      <div className="gate-head">
        {TOOL_ICON[t.tool] ? (() => { const I = TOOL_ICON[t.tool]!; return <I className="icon" />; })() : <Terminal className="icon" />}
        <span style={{ flex: 1 }}>{head}</span>
        {t.status === 'running' && <Spinner />}
      </div>
      <pre className="gate-cmd" style={{ margin: 0 }}>
        <DiffText text={body} />
      </pre>
      {pending && (
        <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {t.reason && <div style={{ fontSize: 12 }} className="muted">{t.reason}</div>}
          <div className="gate-actions">
            {!can.edit && <span className="muted" style={{ fontSize: 13 }}>{VIEWER_HINT}</span>}
            <button className="btn btn-primary btn-wide" style={{ minWidth: 140 }} disabled={!!busy || !can.edit} onClick={() => decide('allow')}>
              Allow once
              {busy === 'allow' ? <Spinner /> : <Check className="icon" />}
            </button>
            <button className="btn btn-secondary" disabled={!!busy || !can.edit} onClick={() => decide('always')}>
              Always allow {GROUP_LABEL[t.toolKey] ?? t.tool} for {agentName}
            </button>
            <button className="btn btn-ghost" disabled={!!busy || !can.edit} onClick={() => decide('deny')}>
              Deny
            </button>
          </div>
        </div>
      )}
      {!pending && t.output && (
        <pre className="gate-out">
          <DiffText text={t.output} />
        </pre>
      )}
    </div>
  );
}

function Note({ icon, children, spinning }: { icon: ReactNode; children: ReactNode; spinning?: boolean }) {
  return (
    <div className="note" role="status">
      {icon}
      <span style={{ flex: 1 }}>{children}</span>
      {spinning && <Spinner />}
    </div>
  );
}

const NOTE_ICONS: Record<string, ReactNode> = {
  bot: <Bot className="icon" />,
  swap: <ArrowLeftRight className="icon" />,
  bookmark: <Bookmark className="icon" />,
  check: <Check className="icon" />,
  github: <GithubIcon />,
  jira: <JiraIcon />,
  alert: <CircleAlert className="icon" />,
  info: <Info className="icon" />,
};

const GATE_ICONS: Record<GateDTO['kind'], ReactNode> = {
  transition: <JiraIcon />,
  ticket: <JiraIcon />,
  plan: <ListChecks className="icon" />,
  push: <GithubIcon />,
  error: <CircleAlert className="icon" />,
};

function GateCard({ gate, project }: { gate: GateDTO; project: ProjectDetail }) {
  const [clicked, setBusy] = useState<'primary' | 'secondary' | null>(null);
  const can = useCan();
  const runBusy = useUI((s) => !!(gate.featureRunId && s.featureBusy[gate.featureRunId]));
  const busy = clicked ?? (runBusy ? 'other' : null);
  const pending = gate.status === 'pending';
  const actions = gateActions(gate, project);
  const run = async (which: 'primary' | 'secondary') => {
    setBusy(which);
    try {
      await (which === 'primary' ? actions.primary() : actions.secondary?.());
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className={`gate ${pending ? 'pending' : ''} ${gate.kind === 'error' ? 'error' : ''}`}>
      <div className="gate-head kick">
        {GATE_ICONS[gate.kind]}
        {gate.kicker}
      </div>
      <div className="gate-body">
        <div>
          <div className="gate-title">{gate.title}</div>
          <div style={{ fontSize: 13, marginTop: 2, whiteSpace: 'pre-wrap' }} className="muted">
            {gate.description}
          </div>
        </div>
        {pending && (
          <div className="gate-actions">
            {!can.edit && <span className="muted" style={{ fontSize: 13 }}>{VIEWER_HINT}</span>}
            <button className="btn btn-primary btn-wide" style={{ minWidth: 170 }} disabled={!!busy || !can.edit} onClick={() => run('primary')}>
              {gate.primaryLabel}
              {busy === 'primary' ? <Spinner /> : <ArrowRight className="icon" />}
            </button>
            {gate.secondaryLabel && actions.secondary && (
              <button className="btn btn-secondary" disabled={!!busy || !can.edit} onClick={() => run('secondary')}>
                {gate.secondaryLabel}
              </button>
            )}
          </div>
        )}
        {!pending && gate.outcome && (
          <div className={`gate-outcome ${gate.status === 'invalidated' || gate.status === 'rejected' ? 'muted' : 'ok'}`}>
            {gate.status === 'invalidated' || gate.status === 'rejected' ? <CircleAlert className="icon" /> : <span className="done-check"><Check className="icon" /></span>}
            {gate.outcome}
          </div>
        )}
      </div>
    </div>
  );
}

function EventView({ e, m, gates, project }: { e: EventPayload; m: MessageDTO; gates: GateDTO[]; project: ProjectDetail }) {
  if (e.kind === 'note') return <Note icon={NOTE_ICONS[e.icon] ?? NOTE_ICONS.info} spinning={e.spinning}>{e.text}</Note>;
  if (e.kind === 'error')
    return (
      <div className="note error" role="alert">
        <CircleAlert className="icon" style={{ marginTop: 2 }} />
        <span style={{ flex: 1 }}>
          {e.text}
          {e.fix && <div style={{ marginTop: 2, color: 'var(--color-neutral-800)' }}>{e.fix}</div>}
        </span>
      </div>
    );
  if (e.kind === 'gate') {
    const g = gates.find((x) => x.id === e.gateId);
    return g ? <GateCard gate={g} project={project} /> : null;
  }
  if (e.kind === 'plan')
    return (
      <AgentFrame m={m}>
        <ol className="plan">
          {e.steps.map((s) => (
            <li key={s.n}>
              <span className="n">{s.n}</span>
              <span>{s.text}</span>
              <span className="tag tag-outline mono">{s.repo}</span>
            </li>
          ))}
        </ol>
      </AgentFrame>
    );
  return null;
}

function AgentFrame({ m, children, actions }: { m: MessageDTO; children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="msg">
      <span className="avatar accent" style={{ fontSize: 12 }}>
        {(m.agentName ?? 'A')[0]}
      </span>
      <div className="msg-body">
        <div className="msg-who">
          <b>{m.agentName ?? 'Agent'}</b>
          {m.model && <span className="tag tag-neutral" style={{ fontWeight: 700 }}>{m.model}</span>}
          {actions}
        </div>
        {children}
      </div>
    </div>
  );
}

function Thinking({ text, live }: { text: string; live: boolean }) {
  return (
    <details className="thinking" open={live && text.length < 600}>
      <summary>
        {live ? <Spinner /> : <ChevronRight className="icon" />}
        {live ? 'Thinking…' : 'Thought process'}
      </summary>
      <div className="t">{text}</div>
    </details>
  );
}

function ForkButton({ m }: { m: MessageDTO }) {
  const nav = useNavigate();
  return (
    <button
      title="Fork from here"
      onClick={async () => {
        try {
          const c = await api.post<{ id: string; projectId: string }>(`/api/conversations/${m.conversationId}/fork`, { messageId: m.id });
          const slug = location.pathname.split('/')[2];
          nav(`/p/${slug}/c/${c.id}`);
          toast('Forked into a new conversation');
        } catch (e) {
          toast(errorText(e), 'error');
        }
      }}
    >
      <GitFork className="icon" style={{ display: 'inline', verticalAlign: -2 }} /> Fork
    </button>
  );
}

/** Wireframes tab: the HTML document shows in the preview, so the chat just notes it. */
const collapseWireframe = (t: string) =>
  t.replace(/```html\s*\n[\s\S]*?(```|$)/gi, (_m, end: string) => `\n\n_${end ? 'Wireframe updated: see the preview._' : 'Drawing the wireframe in the preview…_'}\n\n`);

function AssistantMessage({ m, agents, wireframe }: { m: MessageDTO; agents: AgentConfigDTO[]; wireframe?: boolean }) {
  const text = m.parts.filter((p) => p.type === 'text');
  const thinking = m.parts.filter((p) => p.type === 'thinking').map((p) => (p as { text: string }).text).join('\n');
  const hasText = text.some((p) => (p as { text: string }).text.trim());
  const agentName = m.agentName ?? agents.find((a) => a.id === m.agentId)?.name ?? 'Agent';
  const tools = m.parts.filter((p) => p.type === 'tool_call').map((p) => m.toolCalls.find((t) => t.id === (p as { toolCallId: string }).toolCallId)).filter(Boolean) as ToolCallDTO[];
  // Tool calls that arrived via events before the parts were saved.
  for (const t of m.toolCalls) if (!tools.includes(t)) tools.push(t);
  const textStreamingOnly = m.streaming && !hasText;
  return (
    <>
      {(hasText || thinking || m.streaming) && (
        <AgentFrame m={{ ...m, agentName }} actions={!m.streaming && hasText ? <span className="msg-actions"><ForkButton m={m} /></span> : undefined}>
          {thinking && <Thinking text={thinking} live={m.streaming && !hasText} />}
          {text.map((p, i) => (
            <Markdown key={i} text={wireframe ? collapseWireframe((p as { text: string }).text) : (p as { text: string }).text} />
          ))}
          {m.streaming && (textStreamingOnly && !thinking ? <span><span className="caret" /></span> : hasText ? <span style={{ marginTop: -8 }}><span className="caret" /></span> : null)}
        </AgentFrame>
      )}
      {tools.map((t) =>
        t.status === 'awaiting_approval' || t.approvedBy || t.status === 'denied' ? <ApprovalCard key={t.id} t={t} agentName={agentName} /> : <ToolCard key={t.id} t={t} />,
      )}
    </>
  );
}

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join('') || '?';

function UserMessage({ m, me }: { m: MessageDTO; me: { id: string; initials: string } | null }) {
  const text = m.parts.map((p) => (p.type === 'text' ? p.text : '')).join('\n');
  // Colleagues share conversations: show who wrote it, "You" for your own.
  const mine = !m.author || m.author.id === me?.id;
  return (
    <div className="msg">
      <span className="avatar" title={mine ? undefined : m.author!.name}>{mine ? (me?.initials ?? 'You') : initialsOf(m.author!.name)}</span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="msg-who" style={{ marginBottom: 4 }}>
          <b>{mine ? 'You' : m.author!.name}</b>
          <span className="msg-actions">
            <ForkButton m={m} />
          </span>
        </div>
        <div className="user-text">{text}</div>
      </div>
    </div>
  );
}

export default function Messages({ data, project, onOpenCtx }: { data: ConversationData; project: ProjectDetail; onOpenCtx: (t: CtxTab) => void }) {
  const user = useSession().data?.user;
  const me = user ? { id: user.id, initials: user.initials } : null;
  const { data: agents = [] } = useAgents(project.id);
  void onOpenCtx;
  return (
    <>
      {data.messages.map((m) =>
        m.role === 'user' ? (
          <UserMessage key={m.id} m={m} me={me} />
        ) : m.role === 'assistant' ? (
          <AssistantMessage key={m.id} m={m} agents={agents} wireframe={data.conversation.mode === 'wireframe'} />
        ) : m.event ? (
          <EventView key={m.id} e={m.event} m={m} gates={data.gates} project={project} />
        ) : null,
      )}
    </>
  );
}
